import React, { useState, useEffect } from 'react';
import { ArrowRightLeft, Shield, CheckCircle, AlertCircle, Loader2, X, ChevronRight, Wallet, User, Building2, PenLine, Heart, Users, Link2, Mail, ExternalLink } from 'lucide-react';
import { useTheme } from '../../ThemeContext';
import { ModalShell } from '../ui';
import {
  getMigrationInventory,
  validateDestinationWallet,
  payMigrationFee,
  executeMigration,
  notifyQuarryMembers,
  getMigrationErrorMessage,
  MIGRATION_FEE_ADA,
} from '../../services/walletMigration';
import { getStoredWalletName } from '../../services/ariadne';

/**
 * WalletMigrationModal — Multi-step modal for transferring all wallet data
 *
 * Steps: overview -> connect -> review -> pay -> confirm -> migrating -> success/error
 *        resume (reconnect wallet + enter tx hash for paid-but-incomplete migrations)
 */
const STEPS = ['overview', 'connect', 'review', 'pay', 'confirm', 'migrating', 'success', 'error', 'resume'];

const MIGRATION_STORAGE_KEY = 'limestone_migration_pending';

export default function WalletMigrationModal({ show, onClose, walletAddress, onMigrationComplete }) {
  const { t, isDark } = useTheme();
  const [step, setStep] = useState('overview');
  const [newWalletAddress, setNewWalletAddress] = useState('');
  const [newWalletName, setNewWalletName] = useState('');
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [feeTxHash, setFeeTxHash] = useState('');
  const [migrationResult, setMigrationResult] = useState(null);
  // Resume flow
  const [resumeTxHash, setResumeTxHash] = useState('');
  const [resumeWalletConnected, setResumeWalletConnected] = useState(false);

  // Save migration state to localStorage when fee is paid
  const saveMigrationState = (txHash, oldAddr, newAddr) => {
    try {
      localStorage.setItem(MIGRATION_STORAGE_KEY, JSON.stringify({
        feeTxHash: txHash,
        oldWallet: oldAddr,
        newWallet: newAddr,
        timestamp: Date.now(),
      }));
    } catch (e) { /* localStorage not available */ }
  };

  const clearMigrationState = () => {
    try { localStorage.removeItem(MIGRATION_STORAGE_KEY); } catch (e) { /* ok */ }
  };

  const loadMigrationState = () => {
    try {
      const raw = localStorage.getItem(MIGRATION_STORAGE_KEY);
      if (!raw) return null;
      const state = JSON.parse(raw);
      // Expire after 24 hours
      if (Date.now() - state.timestamp > 24 * 60 * 60 * 1000) {
        localStorage.removeItem(MIGRATION_STORAGE_KEY);
        return null;
      }
      return state;
    } catch (e) { return null; }
  };

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      const saved = loadMigrationState();
      setStep('overview');
      setNewWalletAddress(saved?.newWallet || '');
      setNewWalletName('');
      setInventory(null);
      setLoading(false);
      setError('');
      setFeeTxHash(saved?.feeTxHash || '');
      setMigrationResult(null);
      setResumeTxHash(saved?.feeTxHash || '');
      setResumeWalletConnected(false);
    }
  }, [show]);

  if (!show) return null;

  const currentStepIndex = STEPS.indexOf(step);
  const progressSteps = ['Overview', 'Connect', 'Review', 'Pay', 'Confirm'];

  // ── Connect new wallet (used by both normal flow and resume) ──
  const handleConnectNewWallet = async (walletName, isResume = false) => {
    setLoading(true);
    setError('');
    setNewWalletName(walletName);

    try {
      const cardano = walletName === 'eternl' ? window.cardano?.eternl : window.cardano?.lace;
      if (!cardano) {
        throw new Error(`${walletName === 'lace' ? 'Lace' : 'Eternl'} extension not found`);
      }

      const api = await cardano.enable();
      const usedAddresses = await api.getUsedAddresses();

      if (!usedAddresses || usedAddresses.length === 0) {
        throw new Error('New wallet has no addresses. It needs at least one transaction first.');
      }

      // CIP-30 always returns hex — this is the source of truth
      const address = usedAddresses[0];

      // Validate it's different and fresh
      const validation = await validateDestinationWallet(address, walletAddress);
      if (!validation.valid) {
        throw new Error(validation.reason);
      }

      setNewWalletAddress(address);

      if (isResume) {
        // Resume flow: just mark wallet connected, don't load inventory or advance
        setResumeWalletConnected(true);
      } else {
        // Normal flow: load inventory and advance to review
        const inv = await getMigrationInventory(walletAddress);
        setInventory(inv);
        setStep('review');
      }
    } catch (err) {
      setError(err.message || 'Failed to connect new wallet');
    } finally {
      setLoading(false);
    }
  };

  // ── Pay fee ──
  // No client-side balance gate — wallet extension handles insufficient funds natively
  const handlePayFee = async () => {
    if (feeTxHash) {
      setStep('confirm');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const storedName = getStoredWalletName();
      const result = await payMigrationFee(storedName);
      setFeeTxHash(result.txHash);
      saveMigrationState(result.txHash, walletAddress, newWalletAddress);
      setStep('confirm');
    } catch (err) {
      setError(getMigrationErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Execute migration ──
  const handleExecuteMigration = async () => {
    setStep('migrating');
    setError('');

    try {
      const result = await executeMigration(walletAddress, newWalletAddress, feeTxHash);
      setMigrationResult(result);

      // Send quarry notifications if user had org memberships
      if (inventory?.orgSeats?.length > 0) {
        try {
          await notifyQuarryMembers(inventory.orgSeats, walletAddress, newWalletAddress);
        } catch (notifyErr) {
          console.error('Notification sending failed (non-critical):', notifyErr);
        }
      }

      clearMigrationState();
      setStep('success');
    } catch (err) {
      setError(getMigrationErrorMessage(err));
      setStep('error');
    }
  };

  // ── Resume migration with existing tx hash ──
  // Key difference from old code: newWalletAddress comes from CIP-30 connection (hex guaranteed)
  const handleResumeMigration = async () => {
    if (!resumeTxHash || resumeTxHash.length < 40) {
      setError('Please enter a valid transaction hash (64 hex characters)');
      return;
    }
    if (!newWalletAddress) {
      setError('Please connect your destination wallet first');
      return;
    }

    setFeeTxHash(resumeTxHash);
    setStep('migrating');
    setError('');

    try {
      const result = await executeMigration(walletAddress, newWalletAddress, resumeTxHash);
      setMigrationResult(result);
      clearMigrationState();
      setStep('success');
    } catch (err) {
      setError(getMigrationErrorMessage(err));
      setStep('error');
    }
  };

  // ── Inventory row component ──
  const InventoryRow = ({ icon: Icon, label, count, detail }) => {
    if (count === 0) return null;
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 0',
        borderBottom: `1px solid ${t.border || t.borderStrong}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icon size={16} style={{ color: t.textSecondary }} />
          <span style={{ fontSize: '14px', color: t.text, fontFamily: 'Poppins, sans-serif' }}>{label}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {detail && <span style={{ fontSize: '12px', color: t.textSecondary }}>{detail}</span>}
          <span style={{
            backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(61, 36, 18, 0.06)',
            padding: '2px 10px',
            borderRadius: '999px',
            fontSize: '13px',
            fontWeight: '600',
            color: t.text,
            fontFamily: 'Poppins, sans-serif',
          }}>{count}</span>
        </div>
      </div>
    );
  };

  const truncateAddr = (addr) => addr ? `${addr.substring(0, 8)}...${addr.substring(addr.length - 6)}` : '';

  // ── Shared button styles ──
  const primaryBtnStyle = {
    backgroundColor: t.buttonBg,
    color: t.buttonText,
    border: 'none',
    borderRadius: '12px',
    padding: '14px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontFamily: 'Poppins, sans-serif',
  };

  const secondaryBtnStyle = {
    background: t.buttonSecondaryBg || 'transparent',
    color: t.buttonSecondaryText || t.text,
    border: `1px solid ${t.buttonSecondaryBorder || t.borderStrong}`,
    borderRadius: '8px',
    padding: '12px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
    fontFamily: 'Poppins, sans-serif',
  };

  const hoverLift = (e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.opacity = '0.9'; };
  const hoverReset = (e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.opacity = '1'; };

  return (
    <ModalShell onClose={step !== 'migrating' ? onClose : undefined} maxWidth="520px" blur scrollable zIndex={9999} panelStyle={{ borderRadius: '16px', fontFamily: 'Poppins, sans-serif' }}>
        {/* Close button */}
        {step !== 'migrating' && (
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: '16px', right: '16px',
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '4px', color: t.textSecondary, transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = t.text}
            onMouseLeave={(e) => e.currentTarget.style.color = t.textSecondary}
          >
            <X size={20} />
          </button>
        )}

        {/* Progress indicator */}
        {currentStepIndex >= 0 && currentStepIndex < 5 && step !== 'resume' && (
          <div style={{ display: 'flex', gap: '6px', marginBottom: '28px' }}>
            {progressSteps.map((_, idx) => (
              <div key={idx} style={{
                flex: 1, height: '3px', borderRadius: '999px',
                backgroundColor: idx <= currentStepIndex ? (t.accent || t.buttonBg) : (t.borderStrong || t.border),
                transition: 'background-color 0.3s',
              }} />
            ))}
          </div>
        )}

        {/* ═══════════════════════════════════════ */}
        {/* STEP: OVERVIEW                          */}
        {/* ═══════════════════════════════════════ */}
        {step === 'overview' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                width: '56px', height: '56px', borderRadius: '12px',
                backgroundColor: isDark ? 'rgba(254, 243, 199, 0.08)' : 'rgba(61, 36, 18, 0.06)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <ArrowRightLeft size={28} style={{ color: t.buttonBg }} />
              </div>
              <h2 style={{ fontSize: '24px', fontWeight: '700', color: t.text, margin: '0 0 8px 0' }}>
                Transfer Wallet
              </h2>
              <p style={{ fontSize: '14px', color: t.textSecondary, lineHeight: '1.6' }}>
                Move everything from your current wallet to a new one.
              </p>
            </div>

            <div style={{
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(61, 36, 18, 0.03)',
              borderRadius: '10px', padding: '20px', marginBottom: '20px',
            }}>
              <p style={{ fontSize: '13px', fontWeight: '600', color: t.text, marginBottom: '12px' }}>
                What will transfer:
              </p>
              {[
                'All author profiles and their posts',
                'Organization memberships and seats',
                'Subscriptions and social links',
                'Your complete publishing history',
              ].map((item, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <CheckCircle size={14} style={{ color: t.success || '#16a34a', flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: t.text, lineHeight: '1.4' }}>{item}</span>
                </div>
              ))}
            </div>

            <div style={{
              backgroundColor: isDark ? 'rgba(251, 191, 36, 0.08)' : 'rgba(245, 158, 11, 0.08)',
              border: `1px solid ${isDark ? 'rgba(251, 191, 36, 0.2)' : 'rgba(245, 158, 11, 0.25)'}`,
              borderRadius: '10px', padding: '16px', marginBottom: '24px',
            }}>
              <p style={{ fontSize: '13px', fontWeight: '600', color: t.text, marginBottom: '6px' }}>
                Important:
              </p>
              <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '12px', color: t.textSecondary, lineHeight: '1.7' }}>
                <li>One-time fee of {MIGRATION_FEE_ADA} ADA (paid from current wallet)</li>
                <li>Destination wallet must be fresh (no existing Limestone data)</li>
                <li>Organization Quarry memberships cannot be reconnected after migration</li>
                <li>This action cannot be easily reversed</li>
              </ul>
            </div>

            <button
              onClick={() => setStep('connect')}
              style={{ ...primaryBtnStyle, width: '100%' }}
              onMouseEnter={hoverLift}
              onMouseLeave={hoverReset}
            >
              Begin Transfer <ChevronRight size={18} />
            </button>

            <button
              onClick={onClose}
              style={{
                width: '100%', background: 'none', border: 'none',
                color: t.textSecondary, padding: '12px', cursor: 'pointer',
                fontSize: '13px', marginTop: '8px',
              }}
            >
              Cancel
            </button>

            {/* Resume link */}
            <button
              onClick={() => { setStep('resume'); setError(''); }}
              style={{
                width: '100%', background: 'none', border: 'none',
                color: t.accent || t.buttonBg, padding: '8px', cursor: 'pointer',
                fontSize: '12px', marginTop: '4px', textDecoration: 'underline', opacity: 0.8,
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
            >
              Already paid? Resume a previous migration
            </button>
          </>
        )}

        {/* ═══════════════════════════════════════ */}
        {/* STEP: CONNECT NEW WALLET                */}
        {/* ═══════════════════════════════════════ */}
        {step === 'connect' && (
          <>
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: t.text, margin: '0 0 8px 0' }}>
              Connect New Wallet
            </h2>
            <p style={{ fontSize: '14px', color: t.textSecondary, marginBottom: '24px', lineHeight: '1.5' }}>
              Connect the destination wallet. It must be a fresh wallet with no existing Limestone data.
            </p>

            {/* Current wallet */}
            <div style={{
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(61, 36, 18, 0.03)',
              borderRadius: '8px', padding: '12px 16px', marginBottom: '20px',
              display: 'flex', alignItems: 'center', gap: '10px',
            }}>
              <Wallet size={16} style={{ color: t.textSecondary }} />
              <div>
                <span style={{ fontSize: '11px', color: t.textSecondary, fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Current wallet
                </span>
                <p style={{ fontSize: '13px', color: t.text, fontFamily: 'monospace', margin: '2px 0 0 0' }}>
                  {truncateAddr(walletAddress)}
                </p>
              </div>
            </div>

            {error && (
              <div style={{
                backgroundColor: isDark ? 'rgba(248, 113, 113, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                border: `1px solid ${isDark ? 'rgba(248, 113, 113, 0.25)' : 'rgba(239, 68, 68, 0.25)'}`,
                borderRadius: '8px', padding: '12px 16px', marginBottom: '16px',
              }}>
                <p style={{ fontSize: '13px', color: t.error || '#dc2626', margin: 0, lineHeight: '1.5' }}>{error}</p>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
              {['lace', 'eternl'].map((wName) => (
                <button
                  key={wName}
                  onClick={() => handleConnectNewWallet(wName)}
                  disabled={loading}
                  style={{
                    ...primaryBtnStyle, width: '100%', padding: '16px', fontSize: '16px',
                    opacity: loading ? 0.5 : 1, cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                  onMouseEnter={(e) => !loading && hoverLift(e)}
                  onMouseLeave={(e) => !loading && hoverReset(e)}
                >
                  {loading && newWalletName === wName
                    ? `Connecting to ${wName === 'lace' ? 'Lace' : 'Eternl'}...`
                    : `Connect with ${wName === 'lace' ? 'Lace' : 'Eternl'}`}
                </button>
              ))}
            </div>

            {loading && (
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', color: t.textSecondary }} />
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
              </div>
            )}

            <button
              onClick={() => { setStep('overview'); setError(''); }}
              style={{ ...secondaryBtnStyle, width: '100%', marginTop: '4px' }}
            >
              Back
            </button>
          </>
        )}

        {/* ═══════════════════════════════════════ */}
        {/* STEP: REVIEW INVENTORY                  */}
        {/* ═══════════════════════════════════════ */}
        {step === 'review' && inventory && (
          <>
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: t.text, margin: '0 0 8px 0' }}>
              Review Migration
            </h2>
            <p style={{ fontSize: '14px', color: t.textSecondary, marginBottom: '20px', lineHeight: '1.5' }}>
              Everything below will be transferred to your new wallet.
            </p>

            {/* Transfer visual */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px',
              marginBottom: '24px', padding: '16px',
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(61, 36, 18, 0.03)',
              borderRadius: '10px',
            }}>
              <div style={{ textAlign: 'center' }}>
                <span style={{ fontSize: '11px', color: t.textSecondary, fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '4px' }}>From</span>
                <span style={{ fontSize: '12px', color: t.text, fontFamily: 'monospace' }}>{truncateAddr(walletAddress)}</span>
              </div>
              <ArrowRightLeft size={20} style={{ color: t.buttonBg, flexShrink: 0 }} />
              <div style={{ textAlign: 'center' }}>
                <span style={{ fontSize: '11px', color: t.textSecondary, fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '4px' }}>To</span>
                <span style={{ fontSize: '12px', color: t.success || '#16a34a', fontFamily: 'monospace' }}>{truncateAddr(newWalletAddress)}</span>
              </div>
            </div>

            {/* Inventory */}
            <div style={{ marginBottom: '20px' }}>
              <InventoryRow icon={User} label="Author Profiles" count={inventory.authors.length} detail={inventory.authors.map(a => a.name).join(', ')} />
              <InventoryRow icon={PenLine} label="Posts" count={inventory.posts.length} />
              <InventoryRow icon={Building2} label="Organization Seats" count={inventory.orgSeats.length} detail={inventory.organizations.map(o => o.name).join(', ')} />
              <InventoryRow icon={Heart} label="Subscriptions" count={inventory.subscriptions.length} />
              <InventoryRow icon={Link2} label="Social Links" count={inventory.socialLinks.length} />
              <InventoryRow icon={Mail} label="Org Invites Created" count={inventory.orgInvites.length} />
              <InventoryRow icon={Users} label="Entity Handles" count={inventory.entityHandles.length} />

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', marginTop: '4px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: t.text }}>Total items</span>
                <span style={{ fontSize: '14px', fontWeight: '700', color: t.text }}>
                  {inventory.authors.length + inventory.posts.length + inventory.orgSeats.length + inventory.subscriptions.length + inventory.socialLinks.length + inventory.orgInvites.length + inventory.entityHandles.length}
                </span>
              </div>
            </div>

            {/* Quarry warning */}
            {inventory.orgSeats.length > 0 && (
              <div style={{
                backgroundColor: isDark ? 'rgba(251, 191, 36, 0.08)' : 'rgba(245, 158, 11, 0.08)',
                border: `1px solid ${isDark ? 'rgba(251, 191, 36, 0.2)' : 'rgba(245, 158, 11, 0.25)'}`,
                borderRadius: '8px', padding: '12px 16px', marginBottom: '20px',
              }}>
                <p style={{ fontSize: '12px', fontWeight: '600', color: t.text, marginBottom: '4px' }}>
                  Quarry Notice
                </p>
                <p style={{ fontSize: '12px', color: t.textSecondary, margin: 0, lineHeight: '1.5' }}>
                  Organization members will be notified about your wallet change. Authors cannot be reconnected to the Quarry after migration.
                </p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => { setStep('connect'); setError(''); }}
                style={{ ...secondaryBtnStyle, flex: 1 }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = t.buttonSecondaryBorderHover || t.accent}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = t.buttonSecondaryBorder || t.borderStrong}
              >
                Back
              </button>
              <button
                onClick={() => setStep('pay')}
                style={{ ...primaryBtnStyle, flex: 2 }}
                onMouseEnter={hoverLift}
                onMouseLeave={hoverReset}
              >
                Continue to Payment <ChevronRight size={16} />
              </button>
            </div>
          </>
        )}

        {/* ═══════════════════════════════════════ */}
        {/* STEP: PAY FEE                           */}
        {/* ═══════════════════════════════════════ */}
        {step === 'pay' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Shield size={40} style={{ color: t.buttonBg, marginBottom: '12px' }} />
              <h2 style={{ fontSize: '22px', fontWeight: '700', color: t.text, margin: '0 0 8px 0' }}>
                Migration Fee
              </h2>
              <p style={{ fontSize: '14px', color: t.textSecondary, lineHeight: '1.5' }}>
                A one-time fee is required to process the wallet transfer.
              </p>
            </div>

            {/* Fee breakdown */}
            <div style={{
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(61, 36, 18, 0.03)',
              borderRadius: '10px', padding: '20px', marginBottom: '20px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', color: t.textSecondary }}>Migration fee</span>
                <span style={{ fontSize: '14px', color: t.text, fontFamily: 'monospace', fontWeight: '600' }}>{MIGRATION_FEE_ADA} ADA</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ fontSize: '14px', color: t.textSecondary }}>Network fee (est.)</span>
                <span style={{ fontSize: '14px', color: t.text, fontFamily: 'monospace', fontWeight: '600' }}>~0.2 ADA</span>
              </div>
              <div style={{
                display: 'flex', justifyContent: 'space-between', paddingTop: '10px',
                borderTop: `1px solid ${t.border || t.borderStrong}`,
              }}>
                <span style={{ fontSize: '15px', fontWeight: '700', color: t.text }}>Total</span>
                <span style={{ fontSize: '15px', fontWeight: '700', color: t.text, fontFamily: 'monospace' }}>~{MIGRATION_FEE_ADA + 0.2} ADA</span>
              </div>
            </div>

            {error && (
              <div style={{
                backgroundColor: isDark ? 'rgba(248, 113, 113, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                border: `1px solid ${isDark ? 'rgba(248, 113, 113, 0.25)' : 'rgba(239, 68, 68, 0.25)'}`,
                borderRadius: '8px', padding: '12px 16px', marginBottom: '16px',
              }}>
                <p style={{ fontSize: '13px', color: t.error || '#dc2626', margin: 0, lineHeight: '1.5' }}>{error}</p>
              </div>
            )}

            <p style={{ fontSize: '12px', color: t.textSecondary, marginBottom: '20px', lineHeight: '1.5' }}>
              Your wallet will prompt you to approve the transaction. The fee is paid from your current wallet.
            </p>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => { setStep('review'); setError(''); }}
                style={{ ...secondaryBtnStyle, flex: 1 }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = t.buttonSecondaryBorderHover || t.accent}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = t.buttonSecondaryBorder || t.borderStrong}
              >
                Back
              </button>
              <button
                onClick={handlePayFee}
                disabled={loading}
                style={{
                  ...primaryBtnStyle, flex: 2,
                  opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => !loading && hoverLift(e)}
                onMouseLeave={(e) => !loading && hoverReset(e)}
              >
                {loading ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Waiting for wallet...
                  </>
                ) : 'Pay Fee'}
              </button>
            </div>

            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </>
        )}

        {/* ═══════════════════════════════════════ */}
        {/* STEP: CONFIRM                           */}
        {/* ═══════════════════════════════════════ */}
        {step === 'confirm' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <CheckCircle size={40} style={{ color: t.success || '#16a34a', marginBottom: '12px' }} />
              <h2 style={{ fontSize: '22px', fontWeight: '700', color: t.text, margin: '0 0 8px 0' }}>
                Fee Paid — Confirm Migration
              </h2>
              <p style={{ fontSize: '14px', color: t.textSecondary, lineHeight: '1.5' }}>
                Payment confirmed. Ready to transfer all data.
              </p>
            </div>

            {feeTxHash && (
              <div style={{
                backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(61, 36, 18, 0.03)',
                borderRadius: '8px', padding: '12px 16px', marginBottom: '20px',
              }}>
                <span style={{ fontSize: '11px', fontWeight: '500', color: t.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Fee Transaction
                </span>
                <p style={{ fontSize: '12px', color: t.text, fontFamily: 'monospace', wordBreak: 'break-all', margin: '4px 0 0 0' }}>
                  {feeTxHash}
                </p>
              </div>
            )}

            <div style={{
              backgroundColor: isDark ? 'rgba(248, 113, 113, 0.06)' : 'rgba(239, 68, 68, 0.04)',
              border: `1px solid ${isDark ? 'rgba(248, 113, 113, 0.2)' : 'rgba(239, 68, 68, 0.15)'}`,
              borderRadius: '10px', padding: '16px', marginBottom: '24px',
            }}>
              <p style={{ fontSize: '13px', fontWeight: '600', color: t.error || '#dc2626', marginBottom: '6px' }}>
                Final confirmation
              </p>
              <p style={{ fontSize: '12px', color: t.textSecondary, margin: 0, lineHeight: '1.5' }}>
                This will permanently move all your Limestone data from {truncateAddr(walletAddress)} to {truncateAddr(newWalletAddress)}. This action cannot be easily undone.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={onClose}
                style={{ ...secondaryBtnStyle, flex: 1 }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = t.buttonSecondaryBorderHover || t.accent}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = t.buttonSecondaryBorder || t.borderStrong}
              >
                Cancel
              </button>
              <button
                onClick={handleExecuteMigration}
                style={{ ...primaryBtnStyle, flex: 2 }}
                onMouseEnter={hoverLift}
                onMouseLeave={hoverReset}
              >
                Confirm Transfer
              </button>
            </div>
          </>
        )}

        {/* ═══════════════════════════════════════ */}
        {/* STEP: MIGRATING                         */}
        {/* ═══════════════════════════════════════ */}
        {step === 'migrating' && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <Loader2 size={48} style={{ animation: 'spin 1s linear infinite', color: t.buttonBg, marginBottom: '20px' }} />
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: t.text, margin: '0 0 8px 0' }}>
              Migrating...
            </h2>
            <p style={{ fontSize: '14px', color: t.textSecondary, lineHeight: '1.5', marginBottom: '8px' }}>
              Verifying payment on-chain and transferring data. This can take up to 90 seconds.
            </p>
            <p style={{ fontSize: '12px', color: t.textTertiary }}>
              Do not close this window or disconnect your wallet.
            </p>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* ═══════════════════════════════════════ */}
        {/* STEP: SUCCESS                           */}
        {/* ═══════════════════════════════════════ */}
        {step === 'success' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                backgroundColor: isDark ? 'rgba(74, 222, 128, 0.1)' : 'rgba(22, 163, 74, 0.08)',
                border: `2px solid ${t.success || '#16a34a'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <CheckCircle size={32} style={{ color: t.success || '#16a34a' }} />
              </div>
              <h2 style={{ fontSize: '24px', fontWeight: '700', color: t.text, margin: '0 0 8px 0' }}>
                Migration Complete
              </h2>
              <p style={{ fontSize: '14px', color: t.textSecondary, lineHeight: '1.5' }}>
                All data has been transferred to your new wallet.
              </p>
            </div>

            <div style={{
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(61, 36, 18, 0.03)',
              borderRadius: '10px', padding: '16px', marginBottom: '20px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: t.textSecondary }}>New wallet</span>
                <span style={{ fontSize: '12px', color: t.text, fontFamily: 'monospace' }}>{truncateAddr(newWalletAddress)}</span>
              </div>
              {migrationResult?.migrated && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '13px', color: t.textSecondary }}>Tables migrated</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: t.text }}>{migrationResult.migrated.length}</span>
                </div>
              )}
            </div>

            {feeTxHash && (
              <a
                href={`https://cardanoscan.io/transaction/${feeTxHash}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  fontSize: '13px', color: t.textSecondary, textDecoration: 'none', marginBottom: '20px',
                }}
              >
                View fee transaction <ExternalLink size={12} />
              </a>
            )}

            <p style={{
              fontSize: '13px', color: t.textSecondary, textAlign: 'center',
              marginBottom: '24px', lineHeight: '1.5',
            }}>
              Please disconnect and reconnect with your new wallet to continue using Limestone.
            </p>

            <button
              onClick={() => {
                if (onMigrationComplete) onMigrationComplete(newWalletAddress);
                onClose();
              }}
              style={{ ...primaryBtnStyle, width: '100%' }}
              onMouseEnter={hoverLift}
              onMouseLeave={hoverReset}
            >
              Done
            </button>
          </>
        )}

        {/* ═══════════════════════════════════════ */}
        {/* STEP: ERROR                             */}
        {/* ═══════════════════════════════════════ */}
        {step === 'error' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <AlertCircle size={48} style={{ color: t.error || '#dc2626', marginBottom: '12px' }} />
              <h2 style={{ fontSize: '22px', fontWeight: '700', color: t.text, margin: '0 0 8px 0' }}>
                Migration Failed
              </h2>
            </div>

            <div style={{
              backgroundColor: isDark ? 'rgba(248, 113, 113, 0.08)' : 'rgba(239, 68, 68, 0.08)',
              border: `1px solid ${isDark ? 'rgba(248, 113, 113, 0.25)' : 'rgba(239, 68, 68, 0.25)'}`,
              borderRadius: '8px', padding: '16px', marginBottom: '16px',
            }}>
              <p style={{ fontSize: '14px', color: t.text, margin: 0, lineHeight: '1.6' }}>
                {error}
              </p>
            </div>

            <p style={{ fontSize: '13px', color: t.textSecondary, marginBottom: '24px', lineHeight: '1.5' }}>
              A rollback was attempted to restore your data to the original wallet. Please verify your data in your profile before trying again.
            </p>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={onClose}
                style={{ ...secondaryBtnStyle, flex: 1 }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = t.buttonSecondaryBorderHover || t.accent}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = t.buttonSecondaryBorder || t.borderStrong}
              >
                Close
              </button>
              <button
                onClick={() => {
                  setError('');
                  if (feeTxHash) {
                    handleExecuteMigration();
                  } else {
                    setStep('pay');
                  }
                }}
                style={{ ...primaryBtnStyle, flex: 2 }}
                onMouseEnter={hoverLift}
                onMouseLeave={hoverReset}
              >
                {feeTxHash ? 'Retry Migration' : 'Try Again'}
              </button>
            </div>
          </>
        )}

        {/* ═══════════════════════════════════════ */}
        {/* STEP: RESUME                            */}
        {/* Connect wallet via CIP-30 (no manual    */}
        {/* address input) + enter tx hash          */}
        {/* ═══════════════════════════════════════ */}
        {step === 'resume' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                width: '56px', height: '56px', borderRadius: '12px',
                backgroundColor: isDark ? 'rgba(254, 243, 199, 0.08)' : 'rgba(61, 36, 18, 0.06)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <ArrowRightLeft size={28} style={{ color: t.buttonBg }} />
              </div>
              <h2 style={{ fontSize: '22px', fontWeight: '700', color: t.text, margin: '0 0 8px 0' }}>
                Resume Migration
              </h2>
              <p style={{ fontSize: '14px', color: t.textSecondary, lineHeight: '1.6' }}>
                If you already paid the fee but the migration didn't complete, connect your new wallet and enter the transaction hash below.
              </p>
            </div>

            {error && (
              <div style={{
                backgroundColor: isDark ? 'rgba(248, 113, 113, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                border: `1px solid ${isDark ? 'rgba(248, 113, 113, 0.25)' : 'rgba(239, 68, 68, 0.25)'}`,
                borderRadius: '8px', padding: '12px 16px', marginBottom: '16px',
              }}>
                <p style={{ fontSize: '13px', color: t.error || '#dc2626', margin: 0, lineHeight: '1.5' }}>{error}</p>
              </div>
            )}

            {/* Source wallet (read-only) */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block', fontSize: '12px', fontWeight: '600', color: t.textSecondary,
                textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px',
              }}>
                Source Wallet (current)
              </label>
              <div style={{
                backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(61, 36, 18, 0.03)',
                borderRadius: '8px', padding: '12px 14px', fontSize: '12px',
                color: t.textSecondary, fontFamily: 'monospace', wordBreak: 'break-all',
                border: `1px solid ${t.border || t.borderStrong}`,
              }}>
                {walletAddress || 'Not connected'}
              </div>
            </div>

            {/* Destination wallet — connect via CIP-30, not manual entry */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block', fontSize: '12px', fontWeight: '600', color: t.textSecondary,
                textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px',
              }}>
                Destination Wallet
              </label>

              {resumeWalletConnected && newWalletAddress ? (
                <div style={{
                  backgroundColor: isDark ? 'rgba(74, 222, 128, 0.06)' : 'rgba(22, 163, 74, 0.04)',
                  border: `1px solid ${isDark ? 'rgba(74, 222, 128, 0.2)' : 'rgba(22, 163, 74, 0.15)'}`,
                  borderRadius: '8px', padding: '12px 14px',
                  display: 'flex', alignItems: 'center', gap: '8px',
                }}>
                  <CheckCircle size={14} style={{ color: t.success || '#16a34a', flexShrink: 0 }} />
                  <span style={{ fontSize: '12px', color: t.text, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {truncateAddr(newWalletAddress)}
                  </span>
                  <span style={{
                    fontSize: '11px', color: t.success || '#16a34a', fontWeight: '600',
                    marginLeft: 'auto', whiteSpace: 'nowrap',
                  }}>
                    Connected
                  </span>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['lace', 'eternl'].map((wName) => (
                    <button
                      key={wName}
                      onClick={() => handleConnectNewWallet(wName, true)}
                      disabled={loading}
                      style={{
                        flex: 1,
                        backgroundColor: t.buttonBg,
                        color: t.buttonText,
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.6 : 1,
                        transition: 'all 0.2s',
                        fontFamily: 'Poppins, sans-serif',
                      }}
                    >
                      {loading && newWalletName === wName ? 'Connecting...' : wName === 'lace' ? 'Lace' : 'Eternl'}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Fee tx hash */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block', fontSize: '12px', fontWeight: '600', color: t.textSecondary,
                textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px',
              }}>
                Fee Payment Transaction Hash
              </label>
              <input
                type="text"
                value={resumeTxHash}
                onChange={(e) => setResumeTxHash(e.target.value.trim())}
                placeholder="64-character transaction hash"
                style={{
                  width: '100%',
                  backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(61, 36, 18, 0.03)',
                  border: `1px solid ${t.border || t.borderStrong}`,
                  borderRadius: '8px', padding: '12px 14px', fontSize: '13px',
                  color: t.text, fontFamily: 'monospace', outline: 'none',
                  boxSizing: 'border-box', transition: 'border-color 0.2s',
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = t.accent || t.buttonBg}
                onBlur={(e) => e.currentTarget.style.borderColor = t.border || t.borderStrong}
              />
              <p style={{ fontSize: '11px', color: t.textSecondary, marginTop: '6px', lineHeight: '1.4' }}>
                Find this in your wallet's transaction history or on{' '}
                <a href="https://cardanoscan.io" target="_blank" rel="noopener noreferrer" style={{ color: t.accent || t.buttonBg }}>
                  cardanoscan.io
                </a>
              </p>
            </div>

            <div style={{
              backgroundColor: isDark ? 'rgba(251, 191, 36, 0.08)' : 'rgba(245, 158, 11, 0.08)',
              border: `1px solid ${isDark ? 'rgba(251, 191, 36, 0.2)' : 'rgba(245, 158, 11, 0.25)'}`,
              borderRadius: '8px', padding: '12px 16px', marginBottom: '20px',
            }}>
              <p style={{ fontSize: '12px', color: t.textSecondary, margin: 0, lineHeight: '1.5' }}>
                The server will verify that the transaction sent at least {MIGRATION_FEE_ADA} ADA to the Quarry treasury before proceeding.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => { setStep('overview'); setError(''); setResumeWalletConnected(false); }}
                style={{ ...secondaryBtnStyle, flex: 1 }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = t.buttonSecondaryBorderHover || t.accent}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = t.buttonSecondaryBorder || t.borderStrong}
              >
                Back
              </button>
              <button
                onClick={handleResumeMigration}
                disabled={loading || !resumeTxHash || !resumeWalletConnected}
                style={{
                  ...primaryBtnStyle, flex: 2,
                  opacity: (!resumeTxHash || !resumeWalletConnected) ? 0.5 : 1,
                  cursor: (!resumeTxHash || !resumeWalletConnected) ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => resumeTxHash && resumeWalletConnected && hoverLift(e)}
                onMouseLeave={(e) => resumeTxHash && resumeWalletConnected && hoverReset(e)}
              >
                Resume Migration <ChevronRight size={16} />
              </button>
            </div>
          </>
        )}
    </ModalShell>
  );
}